import { useCallback, useEffect, useState } from "react";
import {
  listServiceLaborRates,
  listServiceOfferings,
  listServiceWorkQueues,
  upsertServiceLaborRate,
} from "@/lib/service/client";
import type { ServiceLaborRate, ServiceLaborRateKind } from "@/lib/service/types";

type Draft = {
  label: string;
  queue_id: string;
  offering_id: string;
  rate_dollars: string;
  rate_kind: ServiceLaborRateKind;
};

const emptyDraft = (): Draft => ({
  label: "",
  queue_id: "",
  offering_id: "",
  rate_dollars: "",
  rate_kind: "flat",
});

export function useServiceLaborRatesPanel(navTick: number, newSignal: number) {
  const [rates, setRates] = useState<ServiceLaborRate[]>([]);
  const [queues, setQueues] = useState<Array<{ id: string; label: string }>>([]);
  const [offerings, setOfferings] = useState<Array<{ id: string; label: string }>>([]);
  const [listBusy, setListBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);

  const reload = useCallback(async () => {
    setListBusy(true);
    setLocalError(null);
    const [ratesRes, queuesRes, offeringsRes] = await Promise.all([
      listServiceLaborRates(),
      listServiceWorkQueues({ active_only: false }),
      listServiceOfferings({ active_only: false }),
    ]);
    setListBusy(false);
    if (!ratesRes.ok) {
      setLocalError(ratesRes.error);
      return;
    }
    if (!queuesRes.ok) {
      setLocalError(queuesRes.error);
      return;
    }
    if (!offeringsRes.ok) {
      setLocalError(offeringsRes.error);
      return;
    }
    setRates(ratesRes.data.labor_rates);
    setQueues(queuesRes.data.queues);
    setOfferings(offeringsRes.data.offerings);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload, navTick]);

  useEffect(() => {
    if (newSignal > 0) setDraft(emptyDraft());
  }, [newSignal]);

  const saveRate = useCallback(async () => {
    const dollars = Number(draft.rate_dollars);
    if (!Number.isFinite(dollars) || dollars < 0) {
      setLocalError("Enter a valid rate in dollars.");
      return;
    }
    setActionBusy(true);
    setLocalError(null);
    const res = await upsertServiceLaborRate({
      label: draft.label.trim(),
      queue_id: draft.queue_id || null,
      offering_id: draft.offering_id || null,
      rate_cents: Math.round(dollars * 100),
      rate_kind: draft.rate_kind,
      is_active: true,
    });
    setActionBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return;
    }
    setDraft(emptyDraft());
    await reload();
  }, [draft, reload]);

  const toggleActive = useCallback(
    async (rate: ServiceLaborRate) => {
      setActionBusy(true);
      const res = await upsertServiceLaborRate({
        id: rate.id,
        label: rate.label,
        queue_id: rate.queue_id,
        offering_id: rate.offering_id,
        rate_cents: rate.rate_cents,
        rate_kind: rate.rate_kind,
        is_active: !rate.is_active,
      });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      await reload();
    },
    [reload]
  );

  return {
    rates,
    queues,
    offerings,
    listBusy,
    actionBusy,
    localError,
    draft,
    setDraft,
    saveRate,
    toggleActive,
    reload,
  };
}
